/**
 * AddBulk API
 * API calls for bulk upload functionality
 */

import { AxiosRequestConfig } from "axios";
import api from "api";
import { SelectOption } from "components/dropdown/Dropdown";
import { endpoints } from "config";
import { UploadResponse } from "./AddBulk.types";

/**
 * Response structure from API
 */
type ApiResponse<T> = {
  status: string;
  details: T;
  errors?: ApiError[];
};

type ApiError = {
  fieldName?: string;
  messageCode?: string;
};

/**
 * Upload CSV file to the server
 * @param payload - Form data payload with CSV file
 * @param endpoint - API endpoint URL
 * @returns Upload response with counts
 */
export const uploadCSV = async (
  payload: Record<string, any>,
  endpoint: string
): Promise<{ success: boolean; data?: UploadResponse; errors?: string[] }> => {
  try {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value));

    const configAndParams: AxiosRequestConfig = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    const result = await api.post<ApiResponse<UploadResponse>>(endpoint, formData, configAndParams);

    if (
      result.data.status === "1" &&
      result.data.errors &&
      Array.isArray(result.data.errors) &&
      result.data.errors.length === 0
    ) {
      return { success: true, data: result.data.details };
    } else {
      return { success: false, errors: parseErrorMessages(result.data) };
    }
  } catch (error: any) {
    console.error("CSV Upload Error:", error);

    if (
      error.response &&
      error.response.data &&
      error.response.data.errors &&
      Array.isArray(error.response.data.errors) &&
      error.response.data.errors.length > 0
    ) {
      const errorMessages = parseErrorMessages(error.response.data);
      return { success: false, errors: errorMessages };
    }

    return { success: false, errors: ["Failed to upload CSV file. Please try again later."] };
  }
};

/**
 * Parse error messages from API response
 * @param responseData - API response data
 * @returns Array of formatted error messages
 */
const parseErrorMessages = (responseData: ApiResponse<any>): string[] => {
  if (!responseData.errors || responseData.errors.length === 0) {
    return ["An unknown error occurred"];
  }

  const allErrorMessages: string[] = [];

  responseData.errors.forEach((item: ApiError) => {
    let errorMessage = "";

    if (item.fieldName && typeof item.fieldName === "string") {
      errorMessage =
        errorMessage +
        item.fieldName
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str: string) => str.toUpperCase()) +
        ":";
    }

    if (item.messageCode && typeof item.messageCode === "string") {
      errorMessage = errorMessage + " " + item.messageCode.replace(/_/g, " ");
    }

    errorMessage = errorMessage.trim();

    if (errorMessage !== "") {
      allErrorMessages.push(errorMessage);
    }
  });

  return allErrorMessages.length > 0 ? allErrorMessages : ["An error occurred during upload"];
};

/**
 * Fetch project data for dropdown
 * @returns Array of project options
 */
export const fetchProjects = async (): Promise<SelectOption[]> => {
  try {
    const response = await api.get<ApiResponse<{ results: SelectOption[] }>>(
      endpoints.contacts.search.projects
    );

    if (
      response &&
      response.data &&
      response.data.status === "1" &&
      response.data.details.results &&
      Array.isArray(response.data.details.results)
    ) {
      return response.data.details.results;
    }

    return [];
  } catch (error) {
    console.error("Error fetching projects:", error);
    return [];
  }
};

/**
 * Fetch site data for dropdown
 * @returns Array of site options
 */
export const fetchSites = async (): Promise<SelectOption[]> => {
  try {
    const response = await api.get<ApiResponse<{ results: SelectOption[] }>>(
      endpoints.contacts.search.sites
    );

    if (
      response &&
      response.data &&
      response.data.status === "1" &&
      response.data.details.results &&
      Array.isArray(response.data.details.results)
    ) {
      return response.data.details.results;
    }

    return [];
  } catch (error) {
    console.error("Error fetching sites:", error);
    return [];
  }
};

/**
 * Fetch programmes for survey dropdown
 * @returns Array of programme options
 */
export const fetchProgrammes = async (): Promise<SelectOption[]> => {
  try {
    const response = await api.get<ApiResponse<{ results: SelectOption[] }>>(
      endpoints.surveys.search.programmes
    );

    if (
      response &&
      response.data &&
      response.data.status === "1" &&
      response.data.details.results &&
      Array.isArray(response.data.details.results)
    ) {
      return response.data.details.results;
    }

    return [];
  } catch (error) {
    console.error("Error fetching programmes:", error);
    return [];
  }
};

/**
 * Fetch survey templates for dropdown
 * @returns Array of survey template options
 */
export const fetchSurveyTemplates = async (): Promise<SelectOption[]> => {
  try {
    const response = await api.get<ApiResponse<{ results: SelectOption[] }>>(
      endpoints.surveys.search.surveyTemplates
    );

    if (
      response &&
      response.data &&
      response.data.status === "1" &&
      response.data.details.results &&
      Array.isArray(response.data.details.results)
    ) {
      return response.data.details.results;
    }

    return [];
  } catch (error) {
    console.error("Error fetching survey templates:", error);
    return [];
  }
};

/**
 * Fetch contact types and contact access types
 * @returns Object with contact types and access types arrays
 */
export const fetchContactTypes = async (): Promise<{
  contactTypes: SelectOption[];
  contactAccessTypes: SelectOption[];
}> => {
  try {
    const response = await api.get<
      ApiResponse<{ contactTypes: SelectOption[]; contactAccessTypes: SelectOption[] }>
    >(endpoints.contacts.getContactTypes);

    if (response && response.data && response.data.status === "1" && response.data.details) {
      const contactTypes =
        Array.isArray(response.data.details.contactTypes) &&
        response.data.details.contactTypes.length > 0
          ? response.data.details.contactTypes
          : [];

      const contactAccessTypes =
        Array.isArray(response.data.details.contactAccessTypes) &&
        response.data.details.contactAccessTypes.length > 0
          ? response.data.details.contactAccessTypes
          : [];

      return { contactTypes, contactAccessTypes };
    }

    return { contactTypes: [], contactAccessTypes: [] };
  } catch (error) {
    console.error("Error fetching contact types:", error);
    return { contactTypes: [], contactAccessTypes: [] };
  }
};
